import type { PlaylistVirtualMediaTemplateDto } from './virtual-media.dto';

export type MediaPermissionPairDto = {
  allowSelfCreate: boolean;
  allowReorder: boolean;
};

export type EventVirtualMediaSettingsDto = {
  allowFlowOverride: boolean;
  allowSessionOverride: boolean;
  flowScopes?: Partial<
    Record<
      'pre_flow' | 'flow_intro' | 'flow_transition' | 'flow_resume' | 'flow_outro' | 'after_flow',
      PlaylistVirtualMediaTemplateDto
    >
  >;
  slotByContentType?: Partial<Record<string, PlaylistVirtualMediaTemplateDto>>;
};

export type FlowVirtualMediaSettingsDto = {
  allowSessionOverride?: boolean;
};

export type EventMediaSettingsDto = {
  hosts: MediaPermissionPairDto & { allowFlowOverride: boolean };
  speakers: {
    allowFlowOverride: boolean;
    allowSessionOverride: boolean;
  };
  virtualMedia?: EventVirtualMediaSettingsDto;
};

/** PATCH body for event media settings (deep partial). */
export type EventMediaSettingsPatchDto = {
  hosts?: Partial<MediaPermissionPairDto & { allowFlowOverride?: boolean }>;
  speakers?: Partial<{
    allowFlowOverride: boolean;
    allowSessionOverride: boolean;
  }>;
  virtualMedia?: Partial<EventVirtualMediaSettingsDto>;
};

export type FlowMediaSettingsDto = {
  hosts?: Partial<MediaPermissionPairDto> & {
    allowScopeOverride?: boolean;
    scopes?: Partial<
      Record<'flow_intro' | 'flow_transition' | 'flow_resume' | 'flow_outro', Partial<MediaPermissionPairDto>>
    >;
  };
  speakers?: {
    allowSessionOverride?: boolean;
  };
  virtualMedia?: FlowVirtualMediaSettingsDto;
};

export type FlowEffectiveMediaSettingsDto = {
  hosts: MediaPermissionPairDto & {
    allowFlowOverride: boolean;
    scopes: Record<'flow_intro' | 'flow_transition' | 'flow_resume' | 'flow_outro', MediaPermissionPairDto>;
  };
  speakers: {
    allowSessionOverride?: boolean;
  };
};

export type FlowMediaSettingsCapabilitiesDto = {
  canEdit: boolean;
  canEditFlowHostDefaults: boolean;
  canEditScopeOverride: boolean;
  canEditSessionOverride: boolean;
  scopes: Record<'flow_intro' | 'flow_transition' | 'flow_resume' | 'flow_outro', { canEdit: boolean }>;
};

export type SessionMediaSettingsDto = {
  speakers?: Partial<MediaPermissionPairDto>;
};

export type SessionEffectiveMediaSettingsDto = {
  speakers: MediaPermissionPairDto;
};

export type SessionMediaSettingsCapabilitiesDto = {
  canEdit: boolean;
  canEditSession: boolean;
};

export type EventMediaSettingsCapabilitiesDto = {
  canEdit: boolean;
};

export type EventSessionTypePolicyDto = {
  offered: boolean;
  allowSelfCreate: boolean;
  allowReorder: boolean;
  virtualMedia?: PlaylistVirtualMediaTemplateDto;
};

export type EventSessionTypeCatalogEntryDto = {
  type: string;
  isEntitled: boolean;
  stored: EventSessionTypePolicyDto;
  effective: EventSessionTypePolicyDto;
  requiredCommercialFeatureKeys?: string[];
};

export type EventSessionTypesSettingsDto = {
  byType: Record<string, EventSessionTypePolicyDto>;
  catalog?: EventSessionTypeCatalogEntryDto[];
  activeCommercialFeatureKeys?: string[];
};

export const CONTRIBUTOR_FLOW_PLAYLIST_SCOPES = ['flow_intro', 'flow_transition', 'flow_resume', 'flow_outro'] as const;

export type ContributorFlowPlaylistScope = (typeof CONTRIBUTOR_FLOW_PLAYLIST_SCOPES)[number];
